/**
 * Feature Permission Matrix
 * 
 * Centralized configuration for all feature access permissions.
 * Maps each feature to required tier, roles, and resources.
 * 
 * @module featurePermissions
 */

import { USER_RESOURCES_LIST } from "./constant";
import { ROLES, TIERS } from "./roleConstants";

/**
 * Feature identifiers - single source of truth for all features
 * Use these constants instead of hardcoded strings throughout the app
 */
export const FEATURES = {
  // Market Profile Charts
  MP_CHART_INDEX: 'mp_chart_index',
  MP_CHART_STOCKS: 'mp_chart_stocks',
  MP_ADVANCED_TOOLS: 'mp_advanced_tools',
  MP_CUSTOM_KRLS: 'mp_custom_krls',
  MP_BOOKMARKS: 'mp_bookmarks',
  
  // Orderflow Charts
  OF_CHART_INDEX: 'of_chart_index',
  OF_CHART_STOCKS: 'of_chart_stocks',
  
  // Line Charts / Candlestick Charts
  LINE_CHART_BASIC: 'line_chart_basic',
  LINE_CHART_ELITE: 'line_chart_elite',
  IV_RANGE_CHART: 'iv_range_chart',
  BALANCE_IMBALANCE: 'balance_imbalance',
  
  // Proprietary Charts (Premium tier)
  NTM_VOLX_CHARTS: 'ntm_volx_charts',
  NTM_OI_CHANGE: 'ntm_oi_change',
  SPECTRUM_CHARTS: 'spectrum_charts',
  GAMMA_DENSITY: 'gamma_density',
  GAMMA_CURVES: 'gamma_curves',
  
  // Data & Tables
  OPTIONS_TABLE: 'options_table',
  GREEKS_TABLE: 'greeks_table',
  INDEX_TABLE: 'index_table',
  POSITION_TABLE: 'position_table',
  FII_DATA: 'fii_data',
  PREMIUM_OPTIONS_TABLE: 'premium_options_table',
  
  // Visualization Charts
  OI_BAR_CHARTS: 'oi_bar_charts',
  IV_CURVES: 'iv_curves',
  SNAKES_CHARTS: 'snakes_charts',
  HV_CHARTS: 'hv_charts',
  GAMMA_CASH_BARS: 'gamma_cash_bars',
  PAYOFF_CHARTS: 'payoff_charts',
  
  // Advanced Features
  MULTI_WINDOW: 'multi_window',
  HISTORICAL_COMPARISON: 'historical_comparison',
  GLOBAL_BOOKMARKS: 'global_bookmarks',
  
  // Broker Integration
  BROKER_LOGIN: 'broker_login',
  
  // UI Tools
  DATE_PICKER: 'date_picker',
  PREVIEW_BUTTON: 'preview_button',
  DRAWING_TOOL: 'drawing_tool',
};

/**
 * Feature Permission Matrix
 * 
 * Each feature maps to:
 * - requiredTier: Minimum tier needed (TIERS.FREE, TIERS.PREMIUM, TIERS.ELITE)
 * - requiredRoles: Array of backend role names (from USER_ROLE_FIELDS)
 * - requiredResources: Array of resource names (from USER_RESOURCES_LIST)
 * - description: Human-readable description
 */
export const FEATURE_PERMISSIONS = {
  // ==================== FREE TIER FEATURES (Basic role) ====================
  // Basic role access: MP Charts, Total OI, Total Volume, IV Curve, OI Viz, 
  // Options Table, Indices Table, Greeks Table, Payoff Charts, 
  // Historical Volatility stats chart, Participantwise positions table, FII data analysis
  
  [FEATURES.MP_CHART_INDEX]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Market Profile charts for Index instruments (delayed data)'
  },
  
  [FEATURES.OPTIONS_TABLE]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Options chain table (Total OI, Total Volume)'
  },
  
  [FEATURES.GREEKS_TABLE]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Greeks analysis table'
  },
  
  [FEATURES.INDEX_TABLE]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Indices Table'
  },
  
  [FEATURES.POSITION_TABLE]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Participantwise positions table'
  },
  
  [FEATURES.FII_DATA]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'FII data analysis'
  },
  
  [FEATURES.IV_CURVES]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'IV Curve charts'
  },
  
  [FEATURES.OI_BAR_CHARTS]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'OI Viz (OI Bar Charts)'
  },
  
  [FEATURES.PAYOFF_CHARTS]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Payoff Charts'
  },
  
  [FEATURES.HV_CHARTS]: {
    requiredTier: TIERS.FREE,
    requiredRoles: [ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Historical Volatility stats chart'
  },

  // ==================== PREMIUM TIER FEATURES ====================
  // Premium role access: All basic role features +
  // NTM OI, NTM Volx, Spectrum, WCash, MP Bookmarks, 
  // Custom & Anchored KRLs, Historical data comparing ability, Multi-window view
  
  [FEATURES.MP_CHART_STOCKS]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [USER_RESOURCES_LIST.MP_STOCKS],
    description: 'Market Profile charts for Stock instruments (requires Stocks FnO add-on)'
  },
  
  [FEATURES.LINE_CHART_BASIC]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Basic candlestick charts with live data'
  },
  
  [FEATURES.NTM_VOLX_CHARTS]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'NTM VolX™ proprietary charts'
  },
  
  [FEATURES.NTM_OI_CHANGE]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'NTM OI Change proprietary charts'
  },
  
  [FEATURES.SPECTRUM_CHARTS]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Spectrum proprietary charts'
  },
  
  [FEATURES.GAMMA_CASH_BARS]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'WCash™ (Gamma Cash bar charts)'
  },
  
  [FEATURES.MP_BOOKMARKS]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'MP Bookmarks'
  },
  
  [FEATURES.MP_CUSTOM_KRLS]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Custom & Anchored KRLs'
  },
  
  [FEATURES.HISTORICAL_COMPARISON]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Historical data comparing ability'
  },
  
  [FEATURES.MULTI_WINDOW]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Multi-window view'
  },
  
  [FEATURES.MP_ADVANCED_TOOLS]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Advanced MP tools and tooltips'
  },
  
  [FEATURES.SNAKES_CHARTS]: {
    requiredTier: TIERS.PREMIUM,
    requiredRoles: [ROLES.PREMIUM, ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'OI Snakes charts'
  },

  // ==================== ELITE TIER FEATURES (Pro1 role) ====================
  // Pro1 role access (Elite vertical): All Premium and basic role features +
  // Orderflow charts, Replay of Orderflow charts, Smart Candlestick charts,
  // Gamma Density and Exposure, Global bookmarks
  
  [FEATURES.OF_CHART_INDEX]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO1, ROLES.PRO2],
    requiredResources: [USER_RESOURCES_LIST.OF_INDEX],
    description: 'Orderflow charts for Index instruments (requires Orderflow add-on)'
  },
  
  [FEATURES.OF_CHART_STOCKS]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO1, ROLES.PRO2],
    requiredResources: [USER_RESOURCES_LIST.OF_STOCKS],
    description: 'Orderflow charts for Stock instruments (requires Orderflow All FnO add-on)'
  },
  
  [FEATURES.LINE_CHART_ELITE]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO1, ROLES.PRO2],
    requiredResources: [USER_RESOURCES_LIST.OF_INDEX],
    description: 'Smart Candlestick™ charts - requires orderflow subscription'
  },

  [FEATURES.IV_RANGE_CHART]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'IV Range charts (NIFTY, SENSEX)'
  },
  
  [FEATURES.BALANCE_IMBALANCE]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO1, ROLES.PRO2],
    requiredResources: [USER_RESOURCES_LIST.OF_INDEX],
    description: 'Replay of Orderflow charts - requires orderflow subscription'
  },
  
  [FEATURES.GAMMA_DENSITY]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Gamma Density and Exposure'
  },
  
  [FEATURES.GAMMA_CURVES]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO1, ROLES.PRO2],
    requiredResources: [],
    description: 'Gamma Exposure Curves'
  },
  
  [FEATURES.GLOBAL_BOOKMARKS]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO1, ROLES.PRO2],
    requiredResources: [USER_RESOURCES_LIST.OF_INDEX],
    description: 'Global bookmarks with drawings'
  },
  
  [FEATURES.BROKER_LOGIN]: {
    requiredTier: TIERS.ELITE,
    requiredRoles: [ROLES.PRO2],
    requiredResources: [],
    description: 'Broker integration login'
  },
};

/**
 * Helper to get all features for a specific tier
 * @param {string} tier - TIERS.FREE, TIERS.PREMIUM, or TIERS.ELITE
 * @returns {Array<string>} Array of feature IDs
 */
export const getFeaturesByTier = (tier) => {
  return Object.entries(FEATURE_PERMISSIONS)
    .filter(([_, perm]) => perm.requiredTier === tier)
    .map(([feature, _]) => feature);
};

/**
 * Helper to check if a feature requires stocks resource
 * @param {string} feature - Feature ID from FEATURES
 * @returns {boolean}
 */
export const requiresStocksResource = (feature) => {
  const perm = FEATURE_PERMISSIONS[feature];
  return perm?.requiredResources.some(r => 
    r === USER_RESOURCES_LIST.MP_STOCKS || r === USER_RESOURCES_LIST.OF_STOCKS
  );
};

/**
 * Helper to check if a feature requires orderflow resource
 * @param {string} feature - Feature ID from FEATURES
 * @returns {boolean}
 */
export const requiresOrderflowResource = (feature) => {
  const perm = FEATURE_PERMISSIONS[feature];
  return perm?.requiredResources.some(r => 
    r === USER_RESOURCES_LIST.OF_INDEX || r === USER_RESOURCES_LIST.OF_STOCKS
  );
};

